import { apiServer } from "../config/config";

export type VenueAssistantVenue = {
  name: string;
  slug: string;
  city?: string;
};

export type VenueAssistantResponse = {
  answer: string;
  found: boolean;
  venue: VenueAssistantVenue | null;
  // Guided-chat follow-ups — short next questions rendered as tappable chips.
  // Optional/defaulted for resilience against older backend responses.
  suggestions?: string[];
  metadata: {
    model: string;
    sessionId: string;
    slug: string;
  };
};

export type ChatApiError = Error & { status?: number };

/**
 * Ask the venue assistant a question about a single venue.
 *
 * Public endpoint (no auth) — we use plain fetch rather than fetchWithToken so
 * it works for logged-out visitors. The backend wraps the microservice payload
 * in a { status, message, data } envelope.
 */
async function askVenueAssistant({
  slug,
  query,
  sessionId,
  visitorId,
}: {
  slug: string;
  query: string;
  sessionId?: string | null;
  visitorId?: string | null;
}): Promise<VenueAssistantResponse> {
  const response = await fetch(`${apiServer}/chat/venue-assistant/ask`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      slug,
      query,
      ...(sessionId ? { sessionId } : {}),
      ...(visitorId ? { visitorId } : {}),
    }),
  });

  const json = await response.json().catch(() => null);

  if (!response.ok || !json || json.status !== "success") {
    const error = new Error(
      json?.message || "The assistant could not respond.",
    ) as ChatApiError;
    error.status = response.status;
    throw error;
  }

  return json.data as VenueAssistantResponse;
}

/**
 * Clear the server-side conversation for a session. Best-effort: clearing is
 * non-critical, so failures are swallowed.
 */
async function clearVenueAssistant({
  sessionId,
  visitorId,
}: {
  sessionId?: string | null;
  visitorId?: string | null;
}): Promise<void> {
  if (!sessionId) {
    return;
  }

  try {
    await fetch(`${apiServer}/chat/venue-assistant/clear`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        sessionId,
        ...(visitorId ? { visitorId } : {}),
      }),
    });
  } catch {
    // best-effort
  }
}

/**
 * Generic chat API client. Capabilities are grouped so future siblings
 * (e.g. chatApi.venueSearch.ask(...)) can be added without breaking changes.
 */
export const chatApi = {
  venueAssistant: {
    ask: askVenueAssistant,
    clear: clearVenueAssistant,
  },
};
